/**
 * @file ShopContractDialog.tsx
 * @description Окно «Отметить договор с платформой» (phase19.28 этап А;
 * PL9, PL18): дата заключения (обязательна, не в будущем) и номер
 * договора (необязательно, до 120 знаков — как CHECK в базе). Дата уходит ISO полночью UTC
 * выбранного дня — день не съезжает от пояса браузера.
 */

import { useState } from "react";

import { ApiError } from "../../../lib/api-client";
import { platformShops } from "../../../lib/api-platform-shops";
import { apiErrorText } from "../../../lib/platform/shop-errors";
import { Alert } from "../../ui/alert";
import { Button } from "../../ui/button";
import { DateField } from "../../ui/DateField";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Label } from "../../ui/label";
import { useNotify } from "../../ui/NotifyProvider";
import { ShopTextField } from "./ShopTextField";

/** Предел номера договора — как CHECK в базе. */
const CONTRACT_NUMBER_MAX = 120;

/** Отказы API (422) → слова. */
const CONTRACT_ERRORS: Readonly<Record<string, string>> = {
  shop_contract_date_invalid: "Дата договора указана неверно",
  shop_contract_number_invalid: "Номер договора — до 120 знаков",
};

/** «гггг-мм-дд» сегодняшнего дня по часам браузера. */
function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

interface Props {
  shopId: string;
  onClose: () => void;
  onSaved: () => void;
}

export function ShopContractDialog({ shopId, onClose, onSaved }: Props) {
  const notify = useNotify();
  const [date, setDate] = useState(todayIso());
  const [number, setNumber] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!date) return setError("Укажите дату договора");
    if (date > todayIso()) return setError("Дата договора не может быть в будущем");
    setBusy(true);
    setError(null);
    try {
      await platformShops.setContract(shopId, { signedAt: `${date}T00:00:00.000Z`, number: number.trim() || null });
      notify.success("Договор отмечен");
      onSaved();
    } catch (err) {
      const known = err instanceof ApiError ? CONTRACT_ERRORS[err.code] : undefined;
      setError(known ?? apiErrorText(err, "Не удалось отметить договор"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && !busy && onClose()}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Договор с платформой</DialogTitle>
          <DialogDescription>Отметьте, что договор (оферта) Commora и поручение на обработку ПДн подписаны.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="contract-date">
              Дата договора<span aria-hidden> *</span>
            </Label>
            <DateField id="contract-date" value={date} onChange={setDate} invalid={!!error && !date} title="Дата договора" />
          </div>
          <ShopTextField id="contract-number" label="Номер договора" value={number} onChange={setNumber} maxLength={CONTRACT_NUMBER_MAX} hint={`Необязательно, до ${CONTRACT_NUMBER_MAX} знаков`} />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button onClick={() => void save()} loading={busy}>
            Отметить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
